// TODO: cleanup with research_assistant_gen_3 ff
//
// OR-6123 (Batch A) — the Research Assistant welcome greeting, rendered
// as the zero-message empty state of a project chat.
//
// Purely presentational: the variant ("Hi!" vs "Hi again!") is decided by
// the project-page caller (where the chats list lives) and handed in as a
// prop. This component never fetches or stores anything — the greeting is
// a client-side render, not a streamed or persisted message (decision
// §3.0).
//
// The copy is QA-locked byte-for-byte (TC-01 / TC-02) and lives in the
// feature locale catalog (``locale/en.ts``) per the repo's react-intl
// convention — one message id per paragraph / per bullet so each string
// stays verbatim and is asserted directly against the catalog.

import { Box, List, ListItem, Stack, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

/**
 * Which greeting variant to render for an empty chat.
 *
 * - ``first`` — the project's first-ever chat ("Hi!").
 * - ``returning`` — every other empty chat ("Hi again!"), including the
 *   all-deleted → re-enter → new-chat case.
 */
export type WelcomeVariant = 'first' | 'returning';

export interface WelcomeMessageProps {
  /** Which greeting to render — ``first`` is the project's first-ever chat. */
  variant: WelcomeVariant;
}

/**
 * Static welcome greeting for an empty chat. Prose uses the same body2
 * variant as agent message prose ({@link MessageBubble}) so the greeting
 * reads as a natural first agent turn even though no message exists.
 */
export const WelcomeMessage = ({ variant }: WelcomeMessageProps) => {
  const intl = useIntl();

  if (variant === 'first') {
    // Bullets are real ``<li>`` elements (not a ``\n- `` blob) so the empty
    // state renders a semantic list and each example asserts verbatim.
    const examples = [
      intl.formatMessage({
        id: 'epiChat.welcome.first.example.population',
        defaultMessage: 'A patient population or cohort you want to identify',
      }),
      intl.formatMessage({
        id: 'epiChat.welcome.first.example.measure',
        defaultMessage: 'An outcome, exposure, or covariate you want to measure',
      }),
      intl.formatMessage({
        id: 'epiChat.welcome.first.example.question',
        defaultMessage: 'A research question you want to explore',
      }),
    ];

    return (
      <Stack spacing={1.5} data-testid="epi-chat-welcome-first">
        <Typography variant="body2" color="text.primary">
          {intl.formatMessage({
            id: 'epiChat.welcome.first.intro',
            defaultMessage:
              "Hi! I'm your Research Assistant and I'm here to help you answer your research questions. Tell me what you're working on and we can get started.",
          })}
        </Typography>
        <Typography variant="body2" color="text.primary">
          {intl.formatMessage({
            id: 'epiChat.welcome.first.examplesLead',
            defaultMessage: 'For example, you could describe:',
          })}
        </Typography>
        <List dense disablePadding sx={{ listStyleType: 'disc', paddingLeft: 3 }}>
          {examples.map((example) => (
            <ListItem key={example} disableGutters sx={{ display: 'list-item', paddingY: 0.25 }}>
              <Typography variant="body2" color="text.primary">
                {example}
              </Typography>
            </ListItem>
          ))}
        </List>
      </Stack>
    );
  }

  return (
    <Box data-testid="epi-chat-welcome-returning">
      <Stack spacing={1.5}>
        <Typography variant="body2" color="text.primary">
          {intl.formatMessage({
            id: 'epiChat.welcome.returning.intro',
            defaultMessage:
              "Hi again! I'm here to help you answer your research questions. I have access to all your project work and can pick up from any previous conversations.",
          })}
        </Typography>
        <Typography variant="body2" color="text.primary">
          {intl.formatMessage({
            id: 'epiChat.welcome.returning.followup',
            defaultMessage: "Tell me what you're working on and we can get started.",
          })}
        </Typography>
      </Stack>
    </Box>
  );
};
